import { EditorState, ContentState, convertToRaw } from "draft-js";
import { useCallback, useEffect, useRef, useState } from "react";
import { Editor } from "react-draft-wysiwyg";
import "react-draft-wysiwyg/dist/react-draft-wysiwyg.css";
import draftToHtml from "draftjs-to-html";
import htmlToDraft from "html-to-draftjs";
import { isTruthy } from "../../helpers/methods";
import { Box, Button, Typography, CircularProgress } from "@mui/material";
import { PictureAsPdf } from "@mui/icons-material";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";
import { CustomButton } from "../../global/components";
import strings from "../../global/constants/StringConstants";
import { DefaultDraftBlockRenderMap } from "draft-js";
import { Map } from "immutable";

const RichTextEditor = ({
  content,
  handleContent,
  contentType,
  title,
  needExportButton,
  fileName,
  fileTitle,
  fileSubTitle,
  defaultImageWidth = 250, // New prop for default image width
  defaultImageHeight = 300, // New prop for default image height
}: {
  content: string;
  handleContent: Function;
  contentType?: string;
  title?: string;
  needExportButton?: boolean;
  fileName?: string;
  fileTitle?: string;
  fileSubTitle?: string;
  defaultImageWidth?: number;
  defaultImageHeight?: number;
}) => {
  const initialHtml = content;
  const [editorState, setEditorState] = useState(EditorState.createEmpty());
  const [text, setText] = useState("");
  const [isExporting, setIsExporting] = useState(false);
  const isInitialized = useRef(false);
  const editorContentRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<any>(null);

  // Handle content updates from parent component
  useEffect(() => {
    if (!isInitialized.current || content !== contentRef.current) {
      const cleanedHtml = content
        .replace(/<li>\s*<p>/g, "<li>")
        .replace(/<\/p>\s*<\/li>/g, "</li>");
      const blocksFromHtml = htmlToDraft(cleanedHtml);
      const { contentBlocks, entityMap } = blocksFromHtml;
      const contentState = ContentState.createFromBlockArray(
        contentBlocks,
        entityMap
      );
      const initialEditorState = EditorState.createWithContent(contentState);
      setEditorState(initialEditorState);
      isInitialized.current = true;
      contentRef.current = content;
    }
  }, [content]);

  // Custom CSS to apply default styling to newly uploaded images
  useEffect(() => {
    // Create a style element to inject custom CSS
    const styleEl = document.createElement("style");
    styleEl.innerHTML = `
      .rdw-editor-main img:not([style*="width"]) {
        width: ${defaultImageWidth}px;
        height: auto;
      }
    `;
    document.head.appendChild(styleEl);

    // Clean up on component unmount
    return () => {
      document.head.removeChild(styleEl);
    };
  }, [defaultImageWidth]);

  const onEditorStateChange = (editorState: EditorState) => {
    setEditorState(editorState);
    const plainText = editorState.getCurrentContent().getPlainText("\u0001");
    setText(plainText);
    const htmlContent = draftToHtml(
      convertToRaw(editorState.getCurrentContent())
    );
    contentRef.current = htmlContent;

    if (isTruthy(contentType)) {
      handleContent(htmlContent, contentType);
    } else {
      handleContent(htmlContent);
    }
  };

  const exportToPdf = async () => {
    setIsExporting(true);

    try {
      const htmlContent = draftToHtml(
        convertToRaw(editorState.getCurrentContent())
      );

      // Create a temporary container with proper styling for PDF
      const container = document.createElement("div");
      container.style.position = "absolute";
      container.style.left = "-99999px";
      container.style.top = "0";
      container.style.width = "210mm"; // A4 width
      container.style.background = "white";
      container.style.overflow = "visible";
      container.style.fontFamily = "Arial, sans-serif";
      container.style.fontSize = "12px";
      container.style.lineHeight = "1.6";
      container.style.color = "#333";

      // Enhanced CSS for better PDF rendering with proper styling preservation and text wrapping
      const style = document.createElement("style");
      style.textContent = `
      .pdf-export-container {
        font-family: Arial, sans-serif;
        font-size: 12px;
        line-height: 1.6;
        color: #333;
        padding: 20mm 15mm;
        width: 210mm;
        box-sizing: border-box;
        word-wrap: break-word;
        overflow-wrap: break-word;
        hyphens: auto;
      }
      
      .pdf-export-container * {
        box-sizing: border-box;
        max-width: 100% !important;
        word-wrap: break-word;
        overflow-wrap: break-word;
      }
      
      /* Headings */
      .pdf-export-container h1 {
        font-size: 24px;
        font-weight: bold;
        margin: 20px 0 15px 0;
        page-break-after: avoid;
        page-break-inside: avoid;
        orphans: 2;
        widows: 2;
      }
      
      .pdf-export-container h2 {
        font-size: 20px;
        font-weight: bold;
        margin: 18px 0 12px 0;
        page-break-after: avoid;
        page-break-inside: avoid;
        orphans: 2;
        widows: 2;
      }
      
      .pdf-export-container h3 {
        font-size: 16px;
        font-weight: bold;
        margin: 15px 0 10px 0;
        page-break-after: avoid;
        page-break-inside: avoid;
        orphans: 2;
        widows: 2;
      }
      
      .pdf-export-container h4 {
        font-size: 14px;
        font-weight: bold;
        margin: 12px 0 8px 0;
        page-break-after: avoid;
        page-break-inside: avoid;
        orphans: 2;
        widows: 2;
      }
      
      /* Paragraphs */
      .pdf-export-container p {
        margin: 0 0 12px 0;
        text-align: justify;
        orphans: 2;
        widows: 2;
        word-wrap: break-word;
        overflow-wrap: break-word;
        hyphens: auto;
      }
      
      /* Lists */
      .pdf-export-container ul,
      .pdf-export-container ol {
        margin: 12px 0;
        padding-left: 25px;
        page-break-inside: avoid;
        orphans: 2;
        widows: 2;
      }
      
      .pdf-export-container ul li {
        list-style-type: disc;
        margin: 6px 0;
        page-break-inside: avoid;
        orphans: 2;
        widows: 2;
        word-wrap: break-word;
        overflow-wrap: break-word;
      }
      
      .pdf-export-container ol li {
        list-style-type: decimal;
        margin: 6px 0;
        page-break-inside: avoid;
        orphans: 2;
        widows: 2;
        word-wrap: break-word;
        overflow-wrap: break-word;
      }
      
      .pdf-export-container ul ul li {
        list-style-type: circle;
      }
      
      .pdf-export-container ul ul ul li {
        list-style-type: square;
      }
      
      /* Text formatting */
      .pdf-export-container strong,
      .pdf-export-container b {
        font-weight: bold;
      }
      
      .pdf-export-container em,
      .pdf-export-container i {
        font-style: italic;
      }
      
      .pdf-export-container u {
        text-decoration: underline;
      }
      
      /* Blockquotes */
      .pdf-export-container blockquote {
        margin: 15px 0;
        padding: 10px 20px;
        border-left: 4px solid #ccc;
        background-color: #f9f9f9;
        font-style: italic;
        page-break-inside: avoid;
        orphans: 2;
        widows: 2;
        word-wrap: break-word;
        overflow-wrap: break-word;
      }
      
      /* Tables */
      .pdf-export-container table {
        width: 100%;
        border-collapse: collapse;
        margin: 15px 0;
        page-break-inside: avoid;
        table-layout: fixed;
      }
      
      .pdf-export-container th,
      .pdf-export-container td {
        border: 1px solid #ddd;
        padding: 8px;
        text-align: left;
        word-wrap: break-word;
        overflow-wrap: break-word;
        vertical-align: top;
      }
      
      .pdf-export-container th {
        background-color: #f2f2f2;
        font-weight: bold;
      }
      
      /* Images */
      .pdf-export-container img {
        max-width: 100% !important;
        height: auto !important;
        margin: 10px 0;
        page-break-inside: avoid;
        display: block;
      }
      
      /* Links */
      .pdf-export-container a {
        color: #0066cc;
        text-decoration: underline;
        word-wrap: break-word;
        overflow-wrap: break-word;
      }
      
      /* Code blocks */
      .pdf-export-container pre,
      .pdf-export-container code {
        font-family: 'Courier New', monospace;
        background-color: #f5f5f5;
        border: 1px solid #ddd;
        border-radius: 3px;
        word-wrap: break-word;
        overflow-wrap: break-word;
        white-space: pre-wrap;
      }
      
      .pdf-export-container pre {
        padding: 10px;
        margin: 15px 0;
        overflow-x: auto;
        page-break-inside: avoid;
        white-space: pre-wrap;
      }
      
      .pdf-export-container code {
        padding: 2px 4px;
      }
      
      /* Page break utilities */
      .page-break-before {
        page-break-before: always;
      }
      
      .page-break-after {
        page-break-after: always;
      }
      
      .no-page-break {
        page-break-inside: avoid;
      }
      
      /* Ensure text doesn't get cut off at margins */
      .pdf-export-container * {
        margin-bottom: 0;
      }
      
      .pdf-export-container p:last-child,
      .pdf-export-container li:last-child,
      .pdf-export-container div:last-child {
        margin-bottom: 0;
      }
      
      /* Print-specific adjustments */
      @media print {
        .pdf-export-container {
          margin: 0;
          padding: 20mm 15mm;
        }
      }
    `;

      document.head.appendChild(style);

      container.className = "pdf-export-container";
      container.innerHTML = htmlContent;
      document.body.appendChild(container);

      await Promise.all(
        Array.from(container.querySelectorAll("img")).map((img) => {
          return new Promise<void>((resolve) => {
            if (
              img.src.startsWith("data:image/svg+xml") ||
              img.src.includes("image/svg+xml")
            ) {
              const svgImg = new Image();
              svgImg.onload = () => {
                const canvas = document.createElement("canvas");
                canvas.width = svgImg.width || 1000;
                canvas.height = svgImg.height || 1000;
                const ctx = canvas.getContext("2d");
                if (ctx) {
                  ctx.drawImage(svgImg, 0, 0);
                  const pngDataUrl = canvas.toDataURL("image/png");
                  img.src = pngDataUrl;
                }
                resolve();
              };
              svgImg.onerror = () => resolve(); // fallback
              svgImg.src = img.src;
            } else {
              resolve();
            }
          });
        })
      );

      // Wait for content to render and fonts to load
      await new Promise((resolve) => setTimeout(resolve, 1500));

      // Configure jsPDF with proper settings
      const pdf = new jsPDF({
        orientation: "portrait",
        unit: "mm",
        format: "a4",
        compress: true,
      });

      // A4 dimensions and margins with extra bottom margin for text safety
      const pageWidth = 210; // A4 width in mm
      const pageHeight = 297; // A4 height in mm
      const margin = {
        top: 20, // Increased from 20
        right: 15, // Increased from 15
        bottom: 35, // Increased from 25
        left: 15, // Increased from 15
      };

      const contentWidth = pageWidth - margin.left - margin.right;
      const contentHeight = pageHeight - margin.top - margin.bottom;

      // Enhanced html2canvas options for better quality
      const canvasOptions = {
        scale: 3, // Higher scale for better quality
        useCORS: true,
        allowTaint: true,
        backgroundColor: "#ffffff",
        logging: false,
        width: container.offsetWidth,
        height: container.offsetHeight,
        windowWidth: container.offsetWidth,
        windowHeight: container.offsetHeight,
        scrollX: 0,
        scrollY: 0,
        removeContainer: false,
      };

      // Generate high-quality canvas
      const canvas = await html2canvas(container, canvasOptions);
      const imgData = canvas.toDataURL("image/png", 1.0);

      // Calculate scaling to fit content properly
      const imgWidth = contentWidth;
      const imgHeight = (canvas.height * contentWidth) / canvas.width;

      // Calculate how many pages we need with safety margin
      const totalPages = Math.ceil(imgHeight / contentHeight);

      // Split content across multiple pages
      for (let page = 0; page < totalPages; page++) {
        if (page > 0) {
          pdf.addPage();
        }

        // Calculate the portion of the image for this page with safety margin
        const sourceY = page * contentHeight * (canvas.height / imgHeight);
        const sourceHeight = Math.min(
          contentHeight * (canvas.height / imgHeight),
          canvas.height - sourceY
        );

        // Create a temporary canvas for this page
        const pageCanvas = document.createElement("canvas");
        const pageCtx: any = pageCanvas.getContext("2d");

        pageCanvas.width = canvas.width;
        pageCanvas.height = sourceHeight;

        // Draw the portion of the main canvas onto the page canvas
        pageCtx.drawImage(
          canvas,
          0,
          sourceY,
          canvas.width,
          sourceHeight,
          0,
          0,
          canvas.width,
          sourceHeight
        );

        const pageImgData = pageCanvas.toDataURL("image/png", 1.0);
        const pageImgHeight = (sourceHeight * contentWidth) / canvas.width;

        // Add the image to the PDF with adjusted positioning
        pdf.addImage(
          pageImgData,
          "PNG",
          margin.left,
          margin.top,
          imgWidth,
          pageImgHeight,
          `page-${page}`,
          "FAST"
        );

        // Add page number with proper positioning to avoid text overlap
        if (totalPages > 1) {
          pdf.setFontSize(10);
          pdf.setTextColor(128, 128, 128);
          const pageNumberText = `Page ${page + 1} of ${totalPages}`;
          const textWidth = pdf.getTextWidth(pageNumberText);
          pdf.text(
            pageNumberText,
            pageWidth - margin.right - textWidth - 5, // Position from right edge
            pageHeight - margin.bottom + 8 // Position above bottom margin
          );
        }
      }

      // Generate filename with timestamp if no fileName provided
      const timestamp = new Date()
        .toISOString()
        .slice(0, 19)
        .replace(/:/g, "-");
      const finalFileName = fileName || `document-${timestamp}`;

      // Save the PDF
      pdf.save(`${finalFileName}.pdf`);

      // Cleanup
      document.body.removeChild(container);
      document.head.removeChild(style);

      console.log("PDF exported successfully!");
    } catch (err: any) {
      console.error("PDF export error:", err);
      alert(`PDF export failed: ${err.message || "Unknown error occurred"}`);
    } finally {
      setIsExporting(false);
    }
  };

  const imageUploadCallback = (
    file: File
  ): Promise<{ data: { link: string } }> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => {
        // Create a new image object to get the natural dimensions
        const img = new Image();
        img.src = reader.result as string;
        img.onload = () => {
          // Calculate dimensions maintaining aspect ratio but within defaults
          let width = defaultImageWidth;
          let height = Math.round((img.height / img.width) * width);

          // If height becomes too large, constrain by height instead
          if (height > defaultImageHeight * 1.5) {
            height = defaultImageHeight;
            width = Math.round((img.width / img.height) * height);
          }

          // Just return the link as required by the type
          resolve({
            data: {
              link: reader.result as string,
            },
          });
        };
      };
      reader.onerror = (error) => reject(error);
      reader.readAsDataURL(file); // Converts file to base64
    });
  };

  return (
    <>
      {needExportButton && (
        <Box
          sx={{
            position: "fixed",
            top: 20,
            right: 24,
            zIndex: 1000,
            borderRadius: "8px",
            padding: "6px 10px",
            transition: "all 0.3s ease-in-out",
          }}
        >
          <CustomButton
            label={isExporting ? "Exporting..." : "Export"}
            type={strings.PRIMARY}
            onClick={exportToPdf}
            icon={
              isExporting ? (
                <CircularProgress
                  size={20}
                  color="inherit"
                  style={{ marginRight: "6px" }}
                />
              ) : (
                <PictureAsPdf style={{ marginRight: "6px" }} />
              )
            }
            disabled={isExporting}
          />
        </Box>
      )}

      {isTruthy(title) && (
        <Typography
          sx={{
            backgroundColor: "white",
            padding: "12px 16px",
            fontSize: "22px",
            fontWeight: 600,
            mb: 2,
            borderBottom: "1px solid #eee",
          }}
        >
          {title}
        </Typography>
      )}

      {/* Editor */}
      <Box
        sx={{
          backgroundColor: "#fff",
          border: "1px solid #ddd",
          borderRadius: "8px",
          padding: "12px",
          maxHeight: "600px",
          overflowY: "auto",
          "&::-webkit-scrollbar": {
            width: "8px",
          },
          "&::-webkit-scrollbar-track": {
            background: "#f0f0f0",
            borderRadius: "10px",
          },
          "&::-webkit-scrollbar-thumb": {
            background: "#c1c1c1",
            borderRadius: "10px",
          },
          "&::-webkit-scrollbar-thumb:hover": {
            background: "#a0a0a0",
          },
        }}
      >
        <Editor
          editorState={editorState}
          onEditorStateChange={onEditorStateChange}
          editorStyle={{
            minHeight: "500px",
            padding: "10px",
            lineHeight: 1.6,
            border: "none",
            overflowY: "auto",
            maxHeight: "600px",
          }}
          toolbarStyle={{
            borderBottom: "1px solid #ddd",
            marginBottom: "8px",
          }}
          wrapperClassName="editor-wrapper"
          toolbar={{
            image: {
              uploadEnabled: true,
              uploadCallback: imageUploadCallback,
              previewImage: true,
              alt: { present: true, mandatory: false },
              defaultSize: {
                height: `${defaultImageHeight}`,
                width: `${defaultImageWidth}`,
              },
              inputAccept: "image/gif,image/jpeg,image/jpg,image/png,image/svg",
              alignmentEnabled: true,
              resizeEnabled: true,
            },
            inline: {
              options: [
                "bold",
                "italic",
                "underline",
                "strikethrough",
                "monospace",
              ],
              inDropdown: false,
            },
          }}
          mention={{
            separator: " ",
            trigger: "@",
            suggestions: [
              { text: "APPLE", value: "apple" },
              { text: "BANANA", value: "banana", url: "banana" },
              { text: "CHERRY", value: "cherry", url: "cherry" },
              { text: "DURIAN", value: "durian", url: "durian" },
              { text: "EGGFRUIT", value: "eggfruit", url: "eggfruit" },
              { text: "FIG", value: "fig", url: "fig" },
              { text: "GRAPEFRUIT", value: "grapefruit", url: "grapefruit" },
              { text: "HONEYDEW", value: "honeydew", url: "honeydew" },
            ],
          }}
        />
      </Box>

      {/* Hidden content for export */}
      <div ref={editorContentRef} style={{ display: "none" }}></div>
    </>
  );
};

export default RichTextEditor;
